import React, { useState, useEffect, useRef } from 'react';

export default function InfoStep({ data, value, onChange, availableLocations, currentLocation, onUpdateLocation }) {
    const dateInputRef = useRef(null);
    const [displayDate, setDisplayDate] = useState('');

    // Helper: YYYY-MM-DD -> DD-MM-YYYY
    const formatDateToDisplay = (isoDate) => {
        if (!isoDate) return '';
        const [y, m, d] = isoDate.split('-');
        return `${d}-${m}-${y}`;
    };

    // Helper: DD-MM-YYYY -> YYYY-MM-DD
    const parseDisplayDate = (displayVal) => {
        const parts = displayVal.split(/[-/]/);
        if (parts.length === 3) {
            let [d, m, y] = parts;
            // Basic sanity check for year length
            if (y.length === 4) {
                return `${y}-${m.padStart(2, '0')}-${d.padStart(2, '0')}`;
            }
        }
        return null;
    };

    // Sync local display state when parent value changes (e.g. initial load or calendar pick)
    useEffect(() => {
        if (value.date) {
            setDisplayDate(formatDateToDisplay(value.date));
        }
    }, [value.date]);

    // Handle manual typing in text field
    const handleDisplayChange = (e) => {
        let val = e.target.value;
        // Allow only numbers and separators
        val = val.replace(/[^0-9-\/]/g, '');
        setDisplayDate(val);

        // Try to parse and update parent if valid
        const isoDate = parseDisplayDate(val);
        if (isoDate && !isNaN(new Date(isoDate).getTime())) {
            onChange({ ...value, date: isoDate });
        }
    };

    // Handle selection from native picker
    const handleCalendarChange = (e) => {
        const isoDate = e.target.value;
        if (isoDate) {
            onChange({ ...value, date: isoDate });
            // Display will auto-update via useEffect
        }
    };

    const DEFAULT_GOALS = [
        "Correctief Onderhoud: Het verhelpen van een acute storing of defect nadat deze is opgetreden.",
        "Preventief Onderhoud: Geplande onderhoudswerkzaamheden om storingen te voorkomen (bijv. smeren, vervangen van slijtdelen, periodieke checks).",
        "Inspectie: Visuele, functionele of technische controle van apparatuur (bijv. periodieke inspectierondes, conditiemeting).",
        "Storing zoeken / Probleemanalyse: Het diagnosticeren van een (terugkerend) probleem waarvan de oorzaak nog onbekend is.",
        "Modificatie / Verbetering: Het aanpassen of optimaliseren van een machine of lijn (bijv. voor efficiëntie, veiligheid of hygiëne).",
        "Nieuwbouw / Installatie: Het plaatsen, aansluiten en in bedrijf stellen van nieuwe apparatuur of productielijnen.",
        "Kalibratie: Het controleren en ijken van meetinstrumenten (zeer belangrijk in food voor o.a. thermometers, weegschalen, metaaldetectoren, flowmeters).",
        "Omstelling / Lijnwissel: Het ombouwen en instellen van een productielijn voor een ander product, formaat of receptuur.",
        "Reiniging / CIP-ondersteuning: Technische assistentie bij (complexe) reinigingsprocessen, of het uitvoeren van specialistisch technisch reinigingswerk.",
        "Kwaliteitsactie (QA/QC): Werkzaamheden direct voortkomend uit een kwaliteitsafwijking, klacht of een controle van de kwaliteitsdienst.",
        "Compliance / Audit-actie: Werkzaamheden die specifiek worden uitgevoerd ter voorbereiding op, of als actiepunt uit, een (externe) audit.",
        "Productieondersteuning: Directe assistentie aan de productielijn om een klein oponthoud te verhelpen of instellingen aan te passen (geen volledige storing)."
    ];

    const goals = (data.dropdowns?.["Doel van de Werkzaamheden:"] && data.dropdowns?.["Doel van de Werkzaamheden:"].length > 0)
        ? data.dropdowns["Doel van de Werkzaamheden:"]
        : DEFAULT_GOALS;

    const handleChange = (field, val) => {
        onChange({ ...value, [field]: val });
    };

    return (
        <div className="fade-in space-y-6">
            <h2 className="text-xl font-bold mb-4 text-white border-b border-slate-700 pb-2">Stap 1: Algemene Informatie</h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
                <div>
                    <label className="label">Ultimo Job Nr.</label>
                    <input
                        type="text"
                        className="input glass-input"
                        placeholder="Bijv. 200345"
                        value={value.ultimoNr || ''}
                        onChange={(e) => handleChange('ultimoNr', e.target.value)}
                    />
                </div>

                <div>
                    <label className="label">Geplande datum van uitvoering (dd-mm-jjjj)</label>
                    <div className="relative" style={{ position: 'relative' }}>
                        <input
                            type="text"
                            className="input glass-input w-full pr-10"
                            placeholder="dd-mm-jjjj"
                            value={displayDate}
                            onChange={handleDisplayChange}
                            maxLength={10}
                        />
                        {/* Hidden native picker for popup */}
                        <input
                            ref={dateInputRef}
                            type="date"
                            className="sr-only"
                            aria-hidden="true"
                            style={{ position: 'absolute', opacity: 0, pointerEvents: 'none', width: 0, height: 0 }}
                            value={value.date || ''}
                            onChange={handleCalendarChange}
                            tabIndex={-1}
                        />
                        {/* Calendar Trigger Button */}
                        <button
                            type="button"
                            className="absolute z-10 text-slate-400 hover:text-blue-400 transition-colors"
                            style={{ right: '12px', top: '50%', transform: 'translateY(-50%)' }}
                            onClick={() => dateInputRef.current?.showPicker()}
                            title="Open Kalender"
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                            </svg>
                        </button>
                    </div>
                </div>
            </div>

            <div className="md:col-span-2">
                <label className="label">Korte Omschrijving</label>
                <input
                    type="text"
                    className="input glass-input"
                    placeholder="Korte beschrijving van de klus..."
                    value={value.description || ''}
                    onChange={(e) => handleChange('description', e.target.value)}
                />
            </div>


            <div className="md:col-span-2">
                <label className="label">Doel van de Werkzaamheden</label>
                <select
                    className="input glass-input"
                    value={value.goal || ''}
                    onChange={(e) => handleChange('goal', e.target.value)}
                >
                    <option value="" className="bg-slate-800">Selecteer een doel...</option>
                    {goals.map((goal, idx) => (
                        <option key={idx} value={goal} className="bg-slate-800">{goal}</option>
                    ))}
                </select>
            </div>

            {/* Location Selector (Only if multiple locations available) */}
            {
                availableLocations && availableLocations.length > 1 && (
                    <div className="md:col-span-2 pt-4 border-t border-slate-700/50">
                        <label className="label text-blue-300">
                            Locatie
                        </label>
                        <select
                            className="input glass-input border-blue-500/30 text-blue-100"
                            value={currentLocation || ''}
                            onChange={(e) => {
                                if (onUpdateLocation) {
                                    if (window.confirm("Weet je zeker dat je van locatie wilt wisselen? Alle ingevulde hiërarchie data wordt gewist.")) {
                                        onUpdateLocation(e.target.value);
                                    }
                                }
                            }}
                        >
                            {availableLocations.map(loc => (
                                <option key={loc} value={loc} className="bg-slate-800">{loc}</option>
                            ))}
                        </select>
                    </div>
                )
            }
        </div >
    );
}
